/**
 * Addon Accessory v2
 * 配件加购板块交互逻辑
 *
 * Features:
 * - Tab 切换
 * - 复选框选择 + 优惠计算
 * - 变体选择器联动
 * - 展开/收起列表
 * - 与主产品一起加入购物车
 */

(function () {
    'use strict';

    // 货币格式化
    const formatMoney = (cents, format) => {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            return Shopify.formatMoney(cents, format || Shopify.money_format);
        }
        // Fallback
        const amount = (cents / 100).toFixed(2);
        return '$' + amount;
    };

    class AddonAccessory {
        constructor(sectionEl) {
            this.section = sectionEl;
            this.sectionId = sectionEl.dataset.sectionId;

            // 配置
            this.config = {
                promoEnabled: sectionEl.dataset.promoEnabled === 'true',
                promoType: sectionEl.dataset.promoType || 'percent',
                promoTargetCount: parseInt(sectionEl.dataset.promoTargetCount, 10) || 3,
                promoDiscountValue: parseFloat(sectionEl.dataset.promoDiscountValue) || 20,
                // 新的高度配置
                collapseHeightDesktop: parseInt(sectionEl.dataset.collapseHeightDesktop, 10) || 240,
                collapseHeightMobile: parseInt(sectionEl.dataset.collapseHeightMobile, 10) || 180,
                defaultExpanded: sectionEl.dataset.defaultExpanded === 'true',
                // 兼容旧版 collapseCount（Section 可能仍在使用）
                collapseCount: parseInt(sectionEl.dataset.collapseCount, 10) || 0,
                showMoreText: sectionEl.dataset.showMoreText || 'Show more',
                showLessText: sectionEl.dataset.showLessText || 'Show less',
                addToCartAction: sectionEl.dataset.addToCartAction || 'drawer',
            };

            // 状态
            this.state = {
                activeTab: 'accessories',
                isExpanded: this.config.defaultExpanded,
                selectedItems: new Set(),
            };

            // Elements
            this.tabs = sectionEl.querySelectorAll('.addon-v2-tab');
            this.panels = sectionEl.querySelectorAll('.addon-v2-panel');
            this.toggleBtn = sectionEl.querySelector('.addon-v2-toggle');
            this.toggleText = sectionEl.querySelector('.addon-v2-toggle-text');
            this.promoText = sectionEl.querySelector('.addon-v2-promo-text');

            this.init();
        }

        init() {
            this.bindTabEvents();
            this.bindCheckboxEvents();
            this.bindVariantEvents();
            this.bindToggleEvents();
            this.bindQuickViewEvents();
            this.bindAddToCartIntercept();
            this.bindResizeEvents();
            this.waitForImagesAndUpdateCollapse();
            this.updatePromoText();
        }

        // 获取当前设备的折叠高度
        getCollapseHeight() {
            // 如果有 collapseCount 配置（兼容 Section），使用旧逻辑
            if (this.config.collapseCount > 0) {
                return 0; // 返回 0 表示使用条目数逻辑
            }
            return window.innerWidth < 768
                ? this.config.collapseHeightMobile
                : this.config.collapseHeightDesktop;
        }

        // 等待图片加载后更新折叠状态
        waitForImagesAndUpdateCollapse() {
            const images = this.section.querySelectorAll('.addon-v2-img');
            if (images.length === 0) {
                this.updateCollapseState();
                return;
            }

            let loadedCount = 0;
            const checkAllLoaded = () => {
                loadedCount++;
                if (loadedCount >= images.length) {
                    this.updateCollapseState();
                }
            };

            images.forEach((img) => {
                if (img.complete) {
                    checkAllLoaded();
                } else {
                    img.addEventListener('load', checkAllLoaded);
                    img.addEventListener('error', checkAllLoaded);
                }
            });

            // Fallback: 500ms 后强制更新
            setTimeout(() => this.updateCollapseState(), 500);
        }

        // 监听窗口 resize
        bindResizeEvents() {
            let resizeTimer;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(() => {
                    this.updateCollapseState();
                }, 150);
            });
        }

        // ===== Tab 切换 =====
        bindTabEvents() {
            this.tabs.forEach((tab) => {
                tab.addEventListener('click', () => {
                    const tabId = tab.dataset.tab;
                    this.switchTab(tabId);
                });
            });
        }

        switchTab(tabId) {
            this.state.activeTab = tabId;

            // Update tabs
            this.tabs.forEach((tab) => {
                const isActive = tab.dataset.tab === tabId;
                tab.classList.toggle('active', isActive);
                tab.setAttribute('aria-selected', String(isActive));
            });

            // Update panels
            this.panels.forEach((panel) => {
                const isActive = panel.dataset.tabContent === tabId;
                panel.classList.toggle('hidden', !isActive);
                panel.hidden = !isActive;
            });

            // Reset collapse state for new tab
            this.state.isExpanded = false;
            this.updateCollapseState();
        }

        // ===== 复选框选择 =====
        bindCheckboxEvents() {
            // 点击整行切换勾选（眼睛图标除外）
            this.section.addEventListener('click', (e) => {
                const card = e.target.closest('.addon-v2-card');
                if (!card) return;

                // 检查是否点击了 data-no-row-click 元素
                if (e.target.closest('[data-no-row-click]')) return;

                const checkbox = card.querySelector('input[name="addon-v2-item"]');
                if (checkbox && !checkbox.disabled) {
                    checkbox.checked = !checkbox.checked;
                    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                }
            });

            // 监听 checkbox 变化
            this.section.addEventListener('change', (e) => {
                if (e.target.matches('input[name="addon-v2-item"]')) {
                    const variantId = e.target.value;
                    if (e.target.checked) {
                        this.state.selectedItems.add(variantId);
                    } else {
                        this.state.selectedItems.delete(variantId);
                    }
                    this.updatePromoText();
                    this.updateDiscountedPrices();
                }
            });
        }

        // ===== 变体选择器 =====
        bindVariantEvents() {
            this.section.addEventListener('change', (e) => {
                if (e.target.matches('.addon-v2-variant-select')) {
                    const card = e.target.closest('.addon-v2-card');
                    const select = e.target;
                    const option = select.options[select.selectedIndex];
                    const checkbox = card.querySelector('input[name="addon-v2-item"]');

                    // Update card data
                    const newVariantId = option.value;
                    const newPrice = option.dataset.price;
                    const newComparePrice = option.dataset.compareAtPrice;

                    card.dataset.variantId = newVariantId;
                    card.dataset.price = newPrice;
                    card.dataset.compareAtPrice = newComparePrice;

                    // Update checkbox value
                    if (checkbox) {
                        const oldValue = checkbox.value;
                        checkbox.value = newVariantId;

                        // Update selected items set
                        if (this.state.selectedItems.has(oldValue)) {
                            this.state.selectedItems.delete(oldValue);
                            this.state.selectedItems.add(newVariantId);
                        }
                    }

                    // Update price display
                    this.updateCardPrice(card, newPrice, newComparePrice);
                    this.updatePromoText();
                }
            });
        }

        updateCardPrice(card, price, comparePrice) {
            const priceNow = card.querySelector('.addon-v2-price-now');
            const priceCompare = card.querySelector('.addon-v2-price-compare');

            if (priceNow) {
                priceNow.textContent = formatMoney(parseInt(price, 10));
            }
            if (priceCompare && comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
                priceCompare.textContent = formatMoney(parseInt(comparePrice, 10));
                priceCompare.style.display = '';
            } else if (priceCompare) {
                priceCompare.style.display = 'none';
            }
        }

        // ===== 优惠文案更新 =====
        updatePromoText() {
            if (!this.promoText) return;

            // Gift Tab 不显示优惠文案
            if (this.state.activeTab === 'gifts') {
                this.promoText.textContent = '';
                return;
            }

            const activePanel = this.section.querySelector(
                `.addon-v2-panel[data-tab-content="${this.state.activeTab}"]`
            );
            if (!activePanel) return;

            const checkedCards = activePanel.querySelectorAll(
                '.addon-v2-card:has(input:checked)'
            );
            const selectedCount = checkedCards.length;

            // 未启用优惠或配置为 0 件（无促销）
            if (!this.config.promoEnabled || this.config.promoTargetCount === 0) {
                this.promoText.textContent = '';
                return;
            }

            const remaining = Math.max(0, this.config.promoTargetCount - selectedCount);

            if (selectedCount === 0) {
                // 未选择时：根据 PRD，未选择时不出现此条文字
                // 但如果需要显示引导，可以显示目标
                if (this.config.promoTargetCount === 1) {
                    // 满1件即减：提示加购1件
                    this.promoText.textContent = `Add 1, Save ${this.config.promoDiscountValue}%`;
                } else {
                    // 其他阶梯
                    this.promoText.textContent = `Add ${this.config.promoTargetCount}, Save ${this.config.promoDiscountValue}%`;
                }
            } else if (remaining > 0) {
                // 部分选择，还需加购
                this.promoText.textContent = `Add ${remaining}, Save ${this.config.promoDiscountValue}%`;
            } else {
                // 满足条件：显示 "立即下单并节省 $XX"
                const totalPrice = this.calculateSelectedTotal(checkedCards);
                const savedAmount =
                    this.config.promoType === 'percent'
                        ? (totalPrice * this.config.promoDiscountValue) / 100
                        : this.config.promoDiscountValue * 100; // fixed amount in cents
                this.promoText.textContent = `Order Now & Save ${formatMoney(savedAmount)}`;
            }
        }

        calculateSelectedTotal(checkedCards) {
            let total = 0;
            checkedCards.forEach((card) => {
                const price = parseInt(card.dataset.price, 10) || 0;
                total += price;
            });
            return total;
        }

        // ===== 折后价格显示更新 =====
        updateDiscountedPrices() {
            if (!this.config.promoEnabled) return;

            // Gift Tab 不触发折后价显示
            if (this.state.activeTab === 'gifts') return;

            const activePanel = this.section.querySelector(
                `.addon-v2-panel[data-tab-content="${this.state.activeTab}"]`
            );
            if (!activePanel) return;

            const checkedCards = activePanel.querySelectorAll(
                '.addon-v2-card:has(input:checked)'
            );
            const selectedCount = checkedCards.length;
            const isDiscountActive = selectedCount >= this.config.promoTargetCount;
            const discountPercent = this.config.promoDiscountValue;

            // 更新所有选中卡片的价格显示
            checkedCards.forEach((card) => {
                const originalPrice = parseInt(card.dataset.price, 10) || 0;
                const priceOriginal = card.querySelector('.addon-v2-price-original');
                const priceDiscounted = card.querySelector('.addon-v2-price-discounted');

                if (isDiscountActive && priceOriginal && priceDiscounted) {
                    // 显示划线价和折后价
                    const discountedPrice = Math.round(originalPrice * (1 - discountPercent / 100));
                    priceOriginal.style.display = '';
                    priceOriginal.textContent = formatMoney(originalPrice);
                    priceDiscounted.textContent = formatMoney(discountedPrice);
                    priceDiscounted.classList.add('addon-v2-price-sale');
                } else if (priceOriginal && priceDiscounted) {
                    // 恢复原价
                    priceOriginal.style.display = 'none';
                    priceDiscounted.textContent = formatMoney(originalPrice);
                    priceDiscounted.classList.remove('addon-v2-price-sale');
                }
            });

            // 未选中的卡片恢复原价显示
            const uncheckedCards = activePanel.querySelectorAll(
                '.addon-v2-card:not(:has(input:checked))'
            );
            uncheckedCards.forEach((card) => {
                const originalPrice = parseInt(card.dataset.price, 10) || 0;
                const priceOriginal = card.querySelector('.addon-v2-price-original');
                const priceDiscounted = card.querySelector('.addon-v2-price-discounted');

                if (priceOriginal && priceDiscounted) {
                    priceOriginal.style.display = 'none';
                    priceDiscounted.textContent = formatMoney(originalPrice);
                    priceDiscounted.classList.remove('addon-v2-price-sale');
                }
            });
        }

        // ===== Toast 提示 =====
        showToast(message, type = 'info') {
            // 移除已有 toast
            const existingToast = document.querySelector('.addon-v2-toast');
            if (existingToast) existingToast.remove();

            const toast = document.createElement('div');
            toast.className = `addon-v2-toast addon-v2-toast--${type}`;
            toast.textContent = message;
            toast.style.cssText = `
                position: fixed;
                bottom: 20px;
                left: 50%;
                transform: translateX(-50%);
                padding: 12px 24px;
                background: ${type === 'error' ? '#dc2626' : type === 'success' ? '#16a34a' : type === 'warning' ? '#f59e0b' : '#333'};
                color: #fff;
                border-radius: 8px;
                font-size: 14px;
                z-index: 10000;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                animation: fadeInUp 0.3s ease;
            `;
            document.body.appendChild(toast);

            setTimeout(() => {
                toast.style.opacity = '0';
                setTimeout(() => toast.remove(), 300);
            }, 3000);
        }

        // ===== 展开/收起 =====
        bindToggleEvents() {
            if (!this.toggleBtn) return;

            this.toggleBtn.addEventListener('click', () => {
                this.state.isExpanded = !this.state.isExpanded;
                this.updateCollapseState();
            });
        }

        updateCollapseState() {
            const activePanel = this.section.querySelector(
                `.addon-v2-panel[data-tab-content="${this.state.activeTab}"]`
            );
            if (!activePanel) return;

            const list = activePanel.querySelector('.addon-v2-list');
            if (!list) return;

            const collapseHeight = this.getCollapseHeight();

            // 如果使用旧的 collapseCount 逻辑（Section 兼容）
            if (collapseHeight === 0 && this.config.collapseCount > 0) {
                const cards = activePanel.querySelectorAll('.addon-v2-card:not([style*="display: none"])');
                const needsCollapse = cards.length > this.config.collapseCount;

                if (this.toggleBtn) {
                    this.toggleBtn.hidden = !needsCollapse;
                    this.toggleBtn.setAttribute('aria-expanded', String(this.state.isExpanded));
                }
                if (this.toggleText) {
                    this.toggleText.textContent = this.state.isExpanded
                        ? this.config.showLessText
                        : this.config.showMoreText;
                }
                if (list) {
                    list.dataset.collapsed = String(!this.state.isExpanded && needsCollapse);
                }
                return;
            }

            // 新的高度判断逻辑
            // 先临时移除高度限制以获取真实高度
            const prevMaxHeight = list.style.maxHeight;
            const prevOverflow = list.style.overflow;
            list.style.maxHeight = 'none';
            list.style.overflow = 'visible';

            const fullHeight = list.scrollHeight;
            const needsCollapse = fullHeight > collapseHeight;

            // Show/hide toggle button
            if (this.toggleBtn) {
                this.toggleBtn.hidden = !needsCollapse;
                this.toggleBtn.setAttribute('aria-expanded', String(this.state.isExpanded));
            }

            // Update toggle text and icon
            if (this.toggleText) {
                this.toggleText.textContent = this.state.isExpanded
                    ? this.config.showLessText
                    : this.config.showMoreText;
            }

            // Apply height constraint
            if (needsCollapse && !this.state.isExpanded) {
                list.style.maxHeight = collapseHeight + 'px';
                list.style.overflow = 'hidden';
                list.dataset.collapsed = 'true';
            } else {
                list.style.maxHeight = 'none';
                list.style.overflow = 'visible';
                list.dataset.collapsed = 'false';
            }
        }

        // ===== Quick View 弹窗 =====
        bindQuickViewEvents() {
            this.section.addEventListener('click', (e) => {
                const btn = e.target.closest('.addon-v2-quick-view');
                if (!btn) return;

                e.preventDefault();
                e.stopPropagation();

                const productHandle = btn.dataset.productHandle;
                if (!productHandle) return;

                // 显示 loading 状态
                btn.style.opacity = '0.5';

                // 获取产品数据并显示弹窗
                this.openQuickViewModal(productHandle, btn);
            });
        }

        // 获取产品数据并打开弹窗
        async openQuickViewModal(handle, triggerBtn) {
            try {
                const res = await fetch(`/products/${handle}.js`);
                if (!res.ok) throw new Error('Product not found');
                const product = await res.json();

                this.renderQuickViewModal(product);
            } catch (err) {
                console.error('[Addon Quick View] Error:', err);
                // Fallback: 打开产品页
                window.open(`/products/${handle}`, '_blank');
            } finally {
                if (triggerBtn) {
                    triggerBtn.style.opacity = '';
                }
            }
        }

        // 渲染弹窗
        renderQuickViewModal(product) {
            // 移除已有弹窗
            const existingModal = document.querySelector('.addon-v2-modal-overlay');
            if (existingModal) existingModal.remove();

            const variant = product.variants[0];
            const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
            const discountPercent = onSale
                ? Math.round((1 - variant.price / variant.compare_at_price) * 100)
                : 0;

            // 构建图片 HTML
            const mainImageSrc = product.images[0] || '';
            const thumbnailsHtml = product.images.slice(0, 4).map((img, idx) => `
                <button class="addon-v2-modal-thumb${idx === 0 ? ' active' : ''}" data-index="${idx}">
                    <img src="${img}" alt="">
                </button>
            `).join('');

            // 构建弹窗 HTML
            // 从 section 读取主色调
            const primaryColor = getComputedStyle(this.section).getPropertyValue('--addon-v2-primary').trim() || '#F97316';

            const modalHtml = `
                <div class="addon-v2-modal-overlay" id="addon-v2-modal" style="--addon-v2-primary: ${primaryColor};">
                    <div class="addon-v2-modal">
                        <button type="button" class="addon-v2-modal-close" aria-label="Close">&times;</button>

                        <div class="addon-v2-modal-images">
                            ${onSale ? `<span class="addon-v2-modal-badge">$${((variant.compare_at_price - variant.price) / 100).toFixed(0)} OFF</span>` : ''}
                            <div class="addon-v2-modal-main-image">
                                <img src="${mainImageSrc}" alt="${product.title}" id="addon-modal-main-img">
                            </div>
                            ${product.images.length > 1 ? `<div class="addon-v2-modal-thumbnails">${thumbnailsHtml}</div>` : ''}
                        </div>

                        <div class="addon-v2-modal-info">
                            <h2 class="addon-v2-modal-title">${product.title}</h2>

                            <div class="addon-v2-modal-price">
                                <span class="addon-v2-modal-price-now">${formatMoney(variant.price)}</span>
                                ${onSale ? `<span class="addon-v2-modal-price-compare">${formatMoney(variant.compare_at_price)}</span>` : ''}
                                ${onSale ? `<span class="addon-v2-modal-price-badge">SALE</span>` : ''}
                            </div>

                            <p class="addon-v2-modal-desc">${product.description ? product.description.substring(0, 200).replace(/<[^>]*>/g, '') + '...' : ''}</p>

                            <a href="/products/${product.handle}" class="addon-v2-modal-view-details">View details</a>

                            <div class="addon-v2-modal-quantity">
                                <span class="addon-v2-modal-quantity-label">Quantity</span>
                                <div class="addon-v2-modal-quantity-selector">
                                    <button type="button" class="addon-v2-modal-qty-btn" data-action="decrease">−</button>
                                    <input type="number" class="addon-v2-modal-qty-input" value="1" min="1" max="99" id="addon-modal-qty">
                                    <button type="button" class="addon-v2-modal-qty-btn" data-action="increase">+</button>
                                </div>
                            </div>

                            <button type="button" class="addon-v2-modal-add-btn" data-variant-id="${variant.id}" ${!variant.available ? 'disabled' : ''}>
                                ${variant.available ? 'Add to cart' : 'Sold out'}
                            </button>
                        </div>
                    </div>
                </div>
            `;

            document.body.insertAdjacentHTML('beforeend', modalHtml);

            const overlay = document.getElementById('addon-v2-modal');
            const modal = overlay.querySelector('.addon-v2-modal');

            // 动画显示
            requestAnimationFrame(() => {
                overlay.classList.add('active');
            });

            // 绑定事件
            this.bindModalEvents(overlay, product);
        }

        // 弹窗内部事件
        bindModalEvents(overlay, product) {
            const modal = overlay.querySelector('.addon-v2-modal');
            const closeBtn = modal.querySelector('.addon-v2-modal-close');
            const mainImg = modal.querySelector('#addon-modal-main-img');
            const thumbnails = modal.querySelectorAll('.addon-v2-modal-thumb');
            const qtyInput = modal.querySelector('#addon-modal-qty');
            const qtyBtns = modal.querySelectorAll('.addon-v2-modal-qty-btn');
            const addBtn = modal.querySelector('.addon-v2-modal-add-btn');

            // 关闭弹窗
            const closeModal = () => {
                overlay.classList.remove('active');
                setTimeout(() => overlay.remove(), 300);
            };

            closeBtn.addEventListener('click', closeModal);
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) closeModal();
            });
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') closeModal();
            }, { once: true });

            // 缩略图切换
            thumbnails.forEach((thumb) => {
                thumb.addEventListener('click', () => {
                    const idx = parseInt(thumb.dataset.index, 10);
                    mainImg.src = product.images[idx];
                    thumbnails.forEach(t => t.classList.remove('active'));
                    thumb.classList.add('active');
                });
            });

            // 数量选择
            qtyBtns.forEach((btn) => {
                btn.addEventListener('click', () => {
                    let val = parseInt(qtyInput.value, 10) || 1;
                    if (btn.dataset.action === 'increase') {
                        val = Math.min(99, val + 1);
                    } else {
                        val = Math.max(1, val - 1);
                    }
                    qtyInput.value = val;
                });
            });

            // 加入购物车
            addBtn.addEventListener('click', async () => {
                const variantId = addBtn.dataset.variantId;
                const qty = parseInt(qtyInput.value, 10) || 1;

                addBtn.disabled = true;
                addBtn.textContent = 'Adding...';

                try {
                    const res = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            items: [{ id: parseInt(variantId, 10), quantity: qty }]
                        })
                    });

                    if (res.ok) {
                        addBtn.textContent = 'Added ✓';
                        setTimeout(() => {
                            closeModal();
                        }, 800);
                    } else {
                        throw new Error('Add failed');
                    }
                } catch (err) {
                    addBtn.textContent = 'Try again';
                    addBtn.disabled = false;
                }
            });
        }

        // ===== 与主产品一起加购 =====
        bindAddToCartIntercept() {
            // 找到页面上的主产品表单
            const productForm = document.querySelector(
                'form[action="/cart/add"], form[action^="/cart/add"]'
            );
            if (!productForm) return;

            productForm.addEventListener('submit', async (e) => {
                try {
                    // 收集选中的配件
                    const selectedItems = this.getSelectedItems();
                    if (selectedItems.length === 0) {
                        // 无配件选中，正常提交
                        return;
                    }

                    e.preventDefault();

                    // 获取主产品信息
                    const formData = new FormData(productForm);
                    const mainId = formData.get('id');
                    const mainQty = parseInt(formData.get('quantity'), 10) || 1;

                    if (!mainId) {
                        productForm.submit();
                        return;
                    }

                    // 过滤不可售的配件
                    const availableItems = [];
                    const unavailableItems = [];

                    selectedItems.forEach((item) => {
                        const card = this.section.querySelector(
                            `.addon-v2-card[data-variant-id="${item.variantId}"]`
                        );
                        const isAvailable = card && card.dataset.available !== 'false';
                        if (isAvailable) {
                            availableItems.push(item);
                        } else {
                            unavailableItems.push(item);
                        }
                    });

                    // 如果有不可售配件，显示提示
                    if (unavailableItems.length > 0) {
                        const skippedCount = unavailableItems.length;
                        this.showToast(
                            `${skippedCount} item(s) unavailable, skipped.`,
                            'warning'
                        );
                    }

                    // 构建购物车 items
                    const items = [
                        { id: parseInt(mainId, 10), quantity: mainQty },
                        ...availableItems.map((item) => ({
                            id: parseInt(item.variantId, 10),
                            quantity: 1,
                        })),
                    ];

                    // 发送请求
                    const res = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Accept: 'application/json',
                        },
                        body: JSON.stringify({ items }),
                    });

                    if (!res.ok) {
                        const errorData = await res.json().catch(() => ({}));
                        // 显示错误信息
                        this.showToast(
                            errorData.description || 'Failed to add to cart',
                            'error'
                        );
                        return;
                    }

                    // 加购成功后的行为
                    this.handleAddToCartSuccess();
                } catch (err) {
                    console.error('[Addon Accessory] Add to cart error:', err);
                    this.showToast('Failed to add to cart', 'error');
                }
            });
        }

        // 加购成功后的处理
        handleAddToCartSuccess() {
            const action = this.config.addToCartAction;

            switch (action) {
                case 'cart':
                    // 跳转购物车
                    window.location.href = '/cart';
                    break;
                case 'drawer':
                    // 打开侧边购物车（使用主题的事件）
                    this.openMiniCart();
                    this.showToast('Added to cart!', 'success');
                    break;
                case 'stay':
                default:
                    // 留在当前页，显示 toast
                    this.showToast('Added to cart!', 'success');
                    break;
            }
        }

        // 打开侧边购物车
        openMiniCart() {
            // 尝试触发主题的 mini cart / drawer cart
            if (typeof _EVENT_HELPER !== 'undefined' && typeof HELPER_UTIL !== 'undefined') {
                HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, document, {
                    drawerType: 'cart',
                });
            } else {
                // Fallback: 触发自定义事件
                document.dispatchEvent(new CustomEvent('cart:open'));
                // 如果没有侧边购物车，跳转购物车页
                setTimeout(() => {
                    if (!document.querySelector('.drawer.drawer-active, [drawer-component].is-open')) {
                        window.location.href = '/cart';
                    }
                }, 300);
            }
        }

        getSelectedItems() {
            const items = [];
            const checkboxes = this.section.querySelectorAll(
                'input[name="addon-v2-item"]:checked'
            );
            checkboxes.forEach((cb) => {
                const card = cb.closest('.addon-v2-card');
                if (card) {
                    items.push({
                        variantId: cb.value,
                        price: parseInt(card.dataset.price, 10) || 0,
                    });
                }
            });
            return items;
        }
    }

    // ===== 初始化 =====
    function initAddonAccessory() {
        document.querySelectorAll('.addon-v2-accessory').forEach((section) => {
            new AddonAccessory(section);
        });
    }

    // DOM Ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAddonAccessory);
    } else {
        initAddonAccessory();
    }

    // Shopify Section reloading (Theme Editor)
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.addEventListener('shopify:section:load', (e) => {
            const section = e.target.querySelector('.addon-v2-accessory');
            if (section) {
                new AddonAccessory(section);
            }
        });
    }
})();
